import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SelectOptionType } from '@/shared/config';
import { getCompactSelectStyles, Icon, Select } from '@/shared/ui';

import styles from './RecordingSettingsPanel.module.scss';

type MicrophoneSettings = {
  options: SelectOptionType[];
  value: string;
  // 0–100.
  level: number;
  disabled: boolean;
  status?: ReactNode;
  // Shown instead of the select until the browser grants access.
  access?: ReactNode;
  onChange: (deviceId: string) => void;
};

type Props = {
  open: boolean;
  language: string;
  languageOptions: SelectOptionType[];
  microphone: MicrophoneSettings;
  disabled?: boolean;
  className?: string;
  onToggle: () => void;
  onLanguageChange: (language: string) => void;
};

// The microphone stays in view in a compact row; the language is preselected
// and kept under the collapsed recording settings.
export const RecordingSettingsPanel: FC<Props> = ({
  open,
  language,
  languageOptions,
  microphone,
  disabled,
  className,
  onToggle,
  onLanguageChange,
}) => {
  const { formatMessage } = useIntl();
  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;
  const languageLabel = languageOptions.find(
    (option) => option.value === language,
  )?.label;

  return (
    <section
      className={cn(styles.panel, className)}
      data-component="transcriptor-recording-settings"
    >
      <div className={styles.row}>
        <Icon name="microphone" size={20} className={styles.icon} />
        <div className={styles.microphone}>
          {microphone.access ?? (
            <Select
              styles={getCompactSelectStyles()}
              isSearchable={false}
              isDisabled={disabled || microphone.disabled}
              aria-label={formatMessage({
                id: 'transcriptorStart.microphone',
                defaultMessage: 'Microphone',
              })}
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
              options={microphone.options}
              value={microphone.value}
              onChange={(value) => {
                if (typeof value === 'string') {
                  microphone.onChange(value);
                }
              }}
            />
          )}
        </div>
        <div
          className={styles.level}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(microphone.level)}
          aria-label={formatMessage({
            id: 'transcriptorRecording.audioLevelLabel',
            defaultMessage: 'Audio Level',
          })}
        >
          <div
            className={styles.levelFill}
            style={{ width: `${Math.min(microphone.level, 100)}%` }}
          />
        </div>
        {microphone.status && (
          <span className={cn('p4', styles.status)}>{microphone.status}</span>
        )}
        <button
          type="button"
          className={cn('p4', styles.toggle)}
          aria-expanded={open}
          onClick={onToggle}
        >
          <FormattedMessage
            id="transcriptorStart.settings"
            defaultMessage="Recording settings"
          />
          {languageLabel && ` · ${languageLabel}`}
          <Icon
            name="arrowDown"
            size={16}
            className={cn(styles.chevron, open && styles.open)}
          />
        </button>
      </div>
      {open && (
        <div className={styles.body}>
          <span className={cn('p4', styles.label)}>
            <FormattedMessage
              id="workspace.language"
              defaultMessage="Language"
            />
          </span>
          <Select
            styles={getCompactSelectStyles()}
            isSearchable={false}
            isDisabled={disabled || languageOptions.length < 2}
            aria-label={formatMessage({
              id: 'workspace.language',
              defaultMessage: 'Language',
            })}
            menuPortalTarget={selectMenuPortalTarget}
            menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
            options={languageOptions}
            value={language}
            onChange={(value) => {
              if (typeof value === 'string') {
                onLanguageChange(value);
              }
            }}
          />
        </div>
      )}
    </section>
  );
};
